import { useRecordContext, useTranslate } from "ra-core";
import { Link } from "react-router";
import { DataTable } from "@/components/admin/data-table";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { Badge } from "@/components/ui/badge";

import type { ChampClass, ClassRosterEntry } from "../types";
import type { RosterStage } from "./classUtils";
import { RosterRowActions } from "./RosterRowActions";

const PlayerField = (_props: { label?: string }) => {
  const record = useRecordContext<ClassRosterEntry>();
  if (!record) return null;
  const name = [record.player_first_name, record.player_last_name]
    .filter(Boolean)
    .join(" ");
  if (!record.player_id) return <span>{name}</span>;
  return (
    <Link
      to={`/contacts/${record.player_id}/show`}
      className="font-medium hover:underline"
      onClick={(event) => event.stopPropagation()}
    >
      {name}
    </Link>
  );
};

const GuardianField = (_props: { label?: string }) => {
  const record = useRecordContext<ClassRosterEntry>();
  if (!record) return null;
  const name = [record.guardian_first_name, record.guardian_last_name]
    .filter(Boolean)
    .join(" ");
  if (!record.guardian_id) return <span>{name}</span>;
  return (
    <Link
      to={`/contacts/${record.guardian_id}/show`}
      className="hover:underline"
    >
      {name}
    </Link>
  );
};

const PhoneField = (_props: { label?: string }) => {
  const record = useRecordContext<ClassRosterEntry>();
  if (!record?.guardian_phone) return null;
  return (
    <a href={`tel:${record.guardian_phone}`} className="hover:underline">
      {record.guardian_phone}
    </a>
  );
};

const PaidField = (_props: { label?: string }) => {
  const record = useRecordContext<ClassRosterEntry>();
  const translate = useTranslate();
  if (!record) return null;
  return record.paid ? (
    <Badge variant="secondary">
      {translate("resources.classes.roster.paid")}
    </Badge>
  ) : (
    <Badge variant="destructive">
      {translate("resources.classes.roster.unpaid")}
    </Badge>
  );
};

// Medical notes first: a coach needs those before anything else.
const NotesField = (_props: { label?: string }) => {
  const record = useRecordContext<ClassRosterEntry>();
  const translate = useTranslate();
  if (!record || (!record.medical_notes && !record.coach_notes)) return null;
  return (
    <div className="flex flex-col gap-1 text-sm max-w-xs">
      {record.medical_notes && (
        <span>
          <span className="font-medium text-destructive">
            {translate("resources.classes.roster.medical")}:
          </span>{" "}
          {record.medical_notes}
        </span>
      )}
      {record.coach_notes && (
        <span>
          <span className="font-medium">
            {translate("resources.classes.roster.coach")}:
          </span>{" "}
          {record.coach_notes}
        </span>
      )}
    </div>
  );
};

const RosterTable = () => (
  <DataTable rowClick={false} bulkActionButtons={false}>
    <DataTable.Col label="resources.classes.roster.columns.player" disableSort>
      <PlayerField />
    </DataTable.Col>
    <DataTable.NumberCol
      source="player_age"
      label="resources.classes.roster.columns.age"
    />
    <DataTable.Col
      source="player_gender"
      label="resources.classes.roster.columns.gender"
    />
    <DataTable.Col
      label="resources.classes.roster.columns.guardian"
      disableSort
    >
      <GuardianField />
    </DataTable.Col>
    <DataTable.Col label="resources.classes.roster.columns.phone" disableSort>
      <PhoneField />
    </DataTable.Col>
    <DataTable.Col source="paid" label="resources.classes.roster.columns.paid">
      <PaidField />
    </DataTable.Col>
    <DataTable.Col
      source="source"
      label="resources.classes.roster.columns.source"
    />
    <DataTable.Col label="resources.classes.roster.columns.notes" disableSort>
      <NotesField />
    </DataTable.Col>
    <DataTable.Col label="resources.classes.roster.columns.actions" disableSort>
      <RosterRowActions />
    </DataTable.Col>
  </DataTable>
);

/** One block of the roster: everyone in this class at a given stage. */
export const RosterSection = ({ stage }: { stage: RosterStage }) => {
  const translate = useTranslate();
  const record = useRecordContext<ChampClass>();
  if (!record) return null;
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-base font-medium">
        {translate(`resources.classes.roster.stages.${stage}`)}
      </h3>
      <ReferenceManyField
        reference="class_roster"
        target="class_id"
        filter={{ stage }}
        sort={{ field: "player_last_name", order: "ASC" }}
        perPage={200}
        empty={
          <p className="text-sm text-muted-foreground">
            {translate("resources.classes.roster.empty")}
          </p>
        }
      >
        <RosterTable />
      </ReferenceManyField>
    </section>
  );
};
